import {
  ANALYTICS_EVENT,
  DiscardCartEndpoint,
  StripePaymentMethods,
  ANALYTICS_PROPERTY,
  IntegrationName,
} from '@kouto/types';
import { PaymentIntentResult } from '@stripe/stripe-js';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import { useIsMobile } from 'WindowDimensionProvider';
import { createBulkBooking, validateRoomChargeAction } from 'actions/booking';
import { resetCart, setCartId } from 'actions/cart';
import { filterEmpty } from 'actions/helpers';
import ApplePayIconSvg from 'assets/icon-apple-pay';
import CardIconSvg from 'assets/icon-card';
import GooglePayIconSvg from 'assets/icon-google-pay';
import { Sep } from 'components/common/styled/common-styled';
import { isIntegratedPaymentMethodEnabledForBrand } from 'components/helpers';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button/Button';
import { Error as FormError } from 'components/theme/Form/Form';
import { usePurchaserFromEmbedConfig } from 'features/EmbedConfig';
import { GuestSurveyModal, useGuestSurveyModal } from 'features/GuestSurvey';
import { PaymentSection } from 'features/ShoppingCart/components/PaymentSection';
import { PurchaserSection } from 'features/ShoppingCart/components/PurchaserSection';
import { PaymentContext } from 'features/ShoppingCart/contexts/PaymentContext';
import { usePaymentOptions } from 'features/ShoppingCart/hooks/usePaymentOptions';
import {
  FieldName,
  IBookingError,
  ICheckoutConfig,
  IExperiencePayload,
} from 'features/ShoppingCart/types';
import { isPMSEnabledForCartExperience } from 'features/ShoppingCart/utils';
import * as Styled from 'features/ShoppingCart/utils/styles';
import {
  creditCardValidation,
  pmsAuthValidation,
  pmsDateValidation,
} from 'features/ShoppingCart/utils/validate';
import { FormProvider, useForm } from 'react-hook-form';
import useCustomHistory from 'hooks/use-custom-history';
import useSearchQueryParams from 'hooks/use-search-params';
import useCartItemsListings from 'hooks/useCartItemsListings';
import useQueryParams from 'hooks/useQueryParams';
import useGenerateListingRecommendations from 'hooks/generateListing/useGenerateListingRecommendations';
import moment from 'moment';
import React, {
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ValidationError } from 'types/api.types';
import { STATUS_PENDING, STATUS_SUCCESS } from 'types/app';
import {
  CREATE_BULK_BOOKING_FAILURE,
  CREATE_PMS_VALIDATION_FAILURE,
} from 'types/booking';
import { PaymentMethod } from 'types/payment';
import {
  formatDateForDisplay,
  isCreditCharge,
  isIntegrationChargeOption,
  isKICCCharge,
  isMemberCharge,
  isRoomCharge,
  serializeParams,
} from 'utils';
import { useDebugLog } from 'utils/debug';
import fetchApi from 'utils/fetch';
import { v4 as uuidv4 } from 'uuid';

import AliPayIcon from 'assets/payment/ali-pay';
import AmazonPayIcon from 'assets/payment/amazon-pay';
import CashAppIcon from 'assets/payment/cash-app';
import LinkPayIcon from 'assets/payment/link-pay';
import WeChatPayIcon from 'assets/payment/we-chat-pay';
import { Alert } from 'components/Alert';
import CartSummary from 'features/ShoppingCart/components/CartSummary/CartSummary';
import useIsEnoughPoints from 'features/ShoppingCart/hooks/useIsEnoughPoints';
import useHyattAuth from 'hooks/useHyattAuth';
import useIsHyattLMSEnabledFlag from 'hooks/useIsHyattLMSEnabledFlag';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import { DiscountType } from '@kouto/cart-prices';
import { analyticsManager } from 'features/analytics/services/AnalyticsManager';
import { summarizeFormErrors } from 'features/analytics/utils/formErrors';
import useHyattCartValidation from 'hooks/useHyattCartValidation';
import { setCookieValue, getCookieValue } from 'utils/handleCookies';
import { HYATT_REDIRECT_ON_LOGIN_LOCAL_KEY } from 'consts/HyattLMSConsts';
import {
  getHyattJoinUrl,
  getHyattSignInUrl,
} from 'components/HyattLMS/getHyattSignInUrl';
import {
  deleteAllCreditCodes,
  deleteDiscountCode,
} from 'actions/discount-code';
import { clearPersistedCheckoutPricing } from 'utils/checkoutPricingStorage';
import { INSUFFICIENT_RESOURCE_ERROR_TYPE } from 'utils/resourceSelections/resourceSelections';
import useCartPaymentIntent from './hooks/useCartPaymentIntent';
import { getCartItemsData } from './checkoutUtils';
import NotEnoughPointsMessage from './NotEnoughPointsMessage';
import { ResourceSoldOutError } from '../../components/ResourceSoldOutError';

const MessageText = styled.p`
  font-size: 14px;
  color: var(--way-colors-primaryTextColor);
`;

const paymentMethodMap: Record<string, string> = {
  [StripePaymentMethods.CASH_APP_PAY]: 'Cash App Pay',
  [StripePaymentMethods.LINK]: 'Link',
  [StripePaymentMethods.ALI_PAY]: 'Alipay',
  [StripePaymentMethods.AMAZON_PAY]: 'Amazon Pay',
  [StripePaymentMethods.WECHAT_PAY]: 'WeChat Pay',
};

interface BulkBookingFailure {
  type: '@booking/create-bulk-booking-failure';
  error: string;
  errorMessage: string;
  errorType?: string;
  payload: {
    validationMessages: IBookingError;
    responseStatus: number;
  };
  cartItemsError: IBookingError;
}

type FilteredPaymentMethod = Exclude<
  PaymentMethod,
  PaymentMethod.KICC | PaymentMethod.HYATT_POINTS
>;
interface LoyaltyProgramData {
  loyaltyProgramData: {
    loyaltyProgramId: string;
    memberId: string | undefined;
  };
  hyattAuthToken: string;
}
interface ICheckoutProps {
  experienceList: IExperiencePayload;
  bookingApprovalError?: ValidationError[];
  onPaymentMethodChange?: () => void;
}

const CheckoutPage = ({
  experienceList,
  bookingApprovalError,
  onPaymentMethodChange,
}: ICheckoutProps) => {
  const { t } = useTranslation();
  const supportedLanguage = useSupportedLanguage();
  const debug = useDebugLog();
  const dispatch = useDispatch();
  const brandId = useBrandId();
  const { theme } = useAppState();
  const isMobile = useIsMobile();
  const paymentOptions = usePaymentOptions();
  const { searchParams } = useSearchQueryParams();
  const { push: customPush, removeSearchQuery } = useCustomHistory();
  const {
    cartTotal,
    isQuotePending,
    confirmCardPaymentCallback,
    submitCardPaymentCallback,
    getPaymentIntentInfoCallback,
  } = useContext(PaymentContext);
  const [bookingIsSuccessful, setBookingIsSuccessful] = useState(false);
  const [bookingError, setBookingError] = useState<IBookingError>(null);
  const [isStripeProcessing, setStripeProcessing] = useState(false);
  const { notEnoughPoints } = useIsEnoughPoints(cartTotal);
  const { discountCode } = useAppState(
    (state: Record<string, unknown>) => state.discountCode,
  );
  const { creditCodes } = useAppState(
    (state: Record<string, unknown>) => state.creditCodes,
  );
  const {
    bookingContext,
    createBulkBookingStatus,
    validatingStatus,
    validationData,
  } = useAppState((state: Record<string, unknown>) => state.booking);
  const { settings, integrations } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const { cartId, cartItems, listings } = useCartItemsListings();
  // A cart whose continuous items have not been quoted totals zero without being free; treating it
  // as complimentary would skip payment entirely.
  const totalCartAmountIsFree = cartTotal === 0 && !isQuotePending;
  const hyattLMSEnabled = useIsHyattLMSEnabledFlag();
  const { getMemberInfo, getAccessToken, session } = useHyattAuth();
  const listingIds = useMemo(() => {
    return listings
      ? Object.values(listings)
          .map((listing) => listing.id)
          .filter((id): id is string => Boolean(id))
      : [];
  }, [listings]);
  const { areAnyEligible } = useHyattCartValidation(listingIds);
  const integrationType = useMemo(() => {
    const firstListing = Object.values(listings)?.[0];
    if (!firstListing) return undefined;

    const groupIds = Object.values(cartItems).map((item) => item.groupId);
    const resourceGroups = firstListing.resourceGroups ?? [];
    const matchingGroupId = resourceGroups.find((group) =>
      groupIds.includes(group.id),
    )?.id;
    const integrationMeta =
      firstListing.integrationMeta?.[0] ||
      resourceGroups.find((group) => group.id === matchingGroupId)
        ?.integrationMeta?.[0] ||
      resourceGroups[0]?.integrationMeta?.[0] ||
      resourceGroups.find((group) => group.id === matchingGroupId)
        ?.experiences?.[0]?.integrationMeta?.[0] ||
      resourceGroups[0]?.experiences?.[0]?.integrationMeta?.[0];
    return integrationMeta?.type;
  }, [listings]);

  const waitlistData = useQueryParams<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phoneNumber?: string;
  }>();
  const embedUserData = usePurchaserFromEmbedConfig();

  const methods = useForm<ICheckoutConfig>({
    mode: 'onChange',
    defaultValues: {
      purchaser: bookingContext?.purchaser ?? embedUserData ?? waitlistData,
      paymentName:
        bookingContext?.purchaser?.lastName ??
        embedUserData?.lastName ??
        waitlistData?.lastName,
      reservationNumber: embedUserData?.reservationNumber,
      activeOption: undefined,
      stripeSelectedMethod: StripePaymentMethods.CARD,
    },
  });

  const {
    clearErrors,
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    setError,
    unregister,
    watch,
  } = methods;
  const selectedPaymentMethod = watch('activeOption');
  const stripeSelectedMethod = watch('stripeSelectedMethod');

  const previousPaymentMethodRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const current =
      typeof selectedPaymentMethod === 'string'
        ? selectedPaymentMethod
        : undefined;
    const previous = previousPaymentMethodRef.current;

    if (previous !== undefined && previous !== current) {
      setBookingError(null);
      Object.values(PaymentMethod).forEach((method) => {
        clearErrors(`root.${method}` as never);
      });
      onPaymentMethodChange?.();
    }
    if (current === PaymentMethod.CREDIT_CARD) {
      // unregistering the payment name and reservation number fields
      // when the payment method is changed to credit card to prevent validation errors
      unregister([FieldName.PAYMENT_NAME, FieldName.RESERVATION_NUMBER], {
        keepDefaultValue: true,
        keepValue: false,
      });
    }

    previousPaymentMethodRef.current = current;
  }, [clearErrors, onPaymentMethodChange, selectedPaymentMethod]);

  const {
    paymentIntent,
    paymentIntentSecret,
    isLoading,
    isValidating,
    error: paymentIntentError,
    freezePaymentIntent,
    unfreezePaymentIntent,
  } = useCartPaymentIntent(cartTotal, selectedPaymentMethod);

  const lockedSecretRef = useRef<string | null | undefined>(null);
  const stablePaymentIntentSecret =
    lockedSecretRef.current ?? paymentIntentSecret;

  const setCreditCardError = () => {
    setError('card', {
      type: 'custom',
      message: creditCardValidation.message,
    });
  };

  const {
    surveyModalOpen,
    presentSurvey,
    onCloseSurveyModal,
    cartId: cartIdForSurvey,
  } = useGuestSurveyModal();

  const { generateRecommendations } = useGenerateListingRecommendations();

  useEffect(() => {
    // Don't run until cart data is loaded and has items
    if (isLoading || !cartItems || Object.keys(cartItems).length === 0) {
      return;
    }

    const cartItemsArray = Object.values(cartItems);

    // Extract session date from first cart item
    const sessionDate = cartItemsArray[0].sessionDateTime;

    // Extract experience IDs and resource group collection IDs
    const experienceIds = cartItemsArray
      .filter((item) => !item.resourceGroupCollectionId)
      .map((item) => item.experienceId);

    const resourceGroupCollectionIds = cartItemsArray
      .map((item) => item.resourceGroupCollectionId)
      .filter((id): id is string => Boolean(id));

    generateRecommendations({
      experienceIds,
      resourceGroupCollectionIds,
      sessionDate,
      brandId,
    });
  }, [cartItems, brandId, isLoading, generateRecommendations]);

  const redirectToConfirmation = async (emailAddress: string) => {
    lockedSecretRef.current = null;
    dispatch(deleteAllCreditCodes());
    dispatch(deleteDiscountCode());
    dispatch(resetCart(brandId));
    localStorage.removeItem('cart');
    clearPersistedCheckoutPricing(brandId);
    sessionStorage.removeItem('price-tier');

    const bookingSuccessConfirmationParams = {
      status: STATUS_SUCCESS,
      emailAddress,
      lang: searchParams?.lang,
      debug: searchParams?.debug,
    };

    window.sessionStorage.setItem('confirmation_email', emailAddress);

    customPush({
      pathname: `/e/booking/confirmation`,
      search: serializeParams(filterEmpty(bookingSuccessConfirmationParams)),
      unset: [
        'mode',
        'noOfGuests',
        'sessionTime',
        'duration',
        'sessionDate',
        'supportedParticipantsCount',
        'email',
        'emailAddress',
        'payment_intent',
        'redirect_status',
        'payment_intent_client_secret',
      ],
    });
  };

  const pickPayloadData = (data: ICheckoutConfig) => {
    const { activeOption } = data;
    if (
      activeOption === PaymentMethod.MEMBER_NUMBER ||
      (activeOption === PaymentMethod.ROOM_CHARGE &&
        !isPMSEnabledForCartExperience({
          activeOption,
          cartItems,
          listings,
        }))
    ) {
      return {
        lastName: data?.paymentName,
        methodNumber: data?.reservationNumber,
      };
    }

    return {
      lastName: data.paymentName || data?.purchaser?.lastName,
      reservationNumber: data.reservationNumber,
    };
  };

  const getValidationData = async (data: ICheckoutConfig) => {
    const response = await dispatch(
      validateRoomChargeAction({
        payload: pickPayloadData(data),
        brandId,
        integrationType,
      }),
    );

    const validationDataResponse =
      response?.payload?.response ?? validationData;

    if (response.type === CREATE_PMS_VALIDATION_FAILURE) {
      return { error: pmsAuthValidation };
    }

    const isDateValid = Object.values(cartItems).every((item) => {
      const experienceDate = moment
        .parseZone(item.sessionDateTime)
        .format('YYYY-MM-DD');

      return moment(experienceDate).isBetween(
        formatDateForDisplay(validationDataResponse?.arrivalDate),
        formatDateForDisplay(validationDataResponse?.departureDate),
        'days',
        '[]',
      );
    });
    if (!isDateValid) {
      return { error: pmsDateValidation };
    }
    return { error: false, validationDataResponse };
  };

  const handlePaymentError = async (
    paymentResult: PaymentIntentResult | null,
    data?: ICheckoutConfig,
    errorMessage = '',
  ) => {
    lockedSecretRef.current = null;
    unfreezePaymentIntent();
    window.focus();
    if (paymentIntent) {
      const discardCartUrl = DiscardCartEndpoint.url({
        brandId,
        cartId,
      }).substring(1);
      await fetchApi.post(discardCartUrl);
    }
    dispatch(
      setCartId({
        brandId,
        cartId: uuidv4(),
      }),
    );
    debug('error', {
      paymentResult,
      formData: data,
    });
    if (
      stripeSelectedMethod !== StripePaymentMethods.APPLE_PAY &&
      stripeSelectedMethod !== StripePaymentMethods.GOOGLE_PAY
    ) {
      setBookingError([
        {
          message: errorMessage.includes('payment_method_data')
            ? 'The provided payment method has failed authentication. Please try again.'
            : errorMessage,
          code: 'booking-stripe-error-stripe-error',
        },
      ]);
    }
  };

  const handlePaymentSuccess = (cartId: string, email: string) => {
    window.focus();
    setBookingIsSuccessful(true);
    presentSurvey(cartId, () => {
      redirectToConfirmation(email);
    });
  };

  const handleStripeRedirection = async () => {
    if (
      searchParams?.payment_intent &&
      searchParams?.redirect_status &&
      searchParams?.payment_intent_client_secret &&
      searchParams?.email
    ) {
      const parent = window.opener || window.parent;

      if (parent.location.origin === window.origin) {
        parent.focus();
        window.close();
      }

      const email = searchParams?.email;

      await confirmPaymentIntentStatus({
        paymentIntentGetter: getPaymentIntentInfoCallback,
        email: email ?? '',
        cartId: searchParams?.cartId ?? cartId,
        clientSecret: searchParams?.payment_intent_client_secret,
      });
    }
  };

  const confirmPaymentIntentStatus = async ({
    paymentIntentGetter,
    clientSecret,
    email,
    cartId,
    data,
  }: {
    paymentIntentGetter: typeof confirmCardPaymentCallback;
    clientSecret: string;
    cartId: string;
    email: string;
    data?: ICheckoutConfig;
  }) => {
    setStripeProcessing(true);
    let paymentResult: PaymentIntentResult | null = null;
    if (!paymentIntentGetter) {
      return;
    }
    try {
      paymentResult = await paymentIntentGetter(clientSecret);

      const failed =
        !paymentResult ||
        paymentResult.error ||
        (paymentResult.paymentIntent &&
          paymentResult.paymentIntent?.status !== 'succeeded');

      if (failed) {
        const message =
          paymentResult?.error?.message ||
          paymentResult?.paymentIntent?.last_payment_error?.message ||
          'Something went wrong. Please try again.';
        await handlePaymentError(paymentResult, data, message);

        return;
      }

      handlePaymentSuccess(cartId, email);
    } catch (err) {
      const error = err as Error;
      const message =
        error?.message || 'Something went wrong with Stripe Payment';
      handlePaymentError(paymentResult, data, message);
    } finally {
      removeSearchQuery(
        'email',
        'payment_intent',
        'redirect_status',
        'payment_intent_client_secret',
      );
      setStripeProcessing(false);
    }
  };

  useEffect(() => {
    if (!getPaymentIntentInfoCallback) {
      return;
    }
    handleStripeRedirection();
  }, [getPaymentIntentInfoCallback]);

  const handlePaymentSubmit = async (
    response: Record<string, string>,
    payload: ICheckoutConfig['purchaser'],
    data: ICheckoutConfig,
  ) => {
    const { activeOption } = data;

    if (isKICCCharge(activeOption)) {
      if (response.clientSecret) {
        const { clientSecret } = response;
        window.location.href = clientSecret;
      }
      // TODO: handle failure
      return;
    }

    if (!confirmCardPaymentCallback) {
      console.error('confirmCardPaymentCallback is not defined');
      unfreezePaymentIntent();
      lockedSecretRef.current = null;
      return;
    }
    await confirmPaymentIntentStatus({
      paymentIntentGetter: confirmCardPaymentCallback,
      clientSecret: response.clientSecret,
      cartId: response.subjectId,
      email: payload.primaryContactEmailAddress || payload.emailAddress,
      data,
    });
  };

  const shouldValidate = (formData: ICheckoutConfig) => {
    const { activeOption, paymentName, reservationNumber } = formData;
    if (activeOption === PaymentMethod.CREDIT_CARD) {
      return (
        !totalCartAmountIsFree &&
        paymentName &&
        reservationNumber &&
        isIntegratedPaymentMethodEnabledForBrand(integrations, settings)
      );
    }

    if (activeOption === PaymentMethod.ROOM_CHARGE) {
      const roomChargeConfig = paymentOptions.find((p) => {
        return p.name === PaymentMethod.ROOM_CHARGE;
      });
      if (
        totalCartAmountIsFree &&
        !roomChargeConfig?.shouldValidateComplimentaryBooking
      ) {
        return false;
      }
      return isIntegratedPaymentMethodEnabledForBrand(integrations, settings);
    }

    return false;
  };

  const handleBookingError = (
    formData: ICheckoutConfig,
    bookingResponse: BulkBookingFailure,
  ) => {
    const { activeOption } = formData;

    const {
      payload: { validationMessages },
      cartItemsError,
    } = bookingResponse;

    const errorMessages: Record<FilteredPaymentMethod, string> = {
      [PaymentMethod.CREDIT_CARD]: t('bookBulkErrorByCard'),
      [PaymentMethod.ROOM_CHARGE]: t('bookBulkErrorByRoomCharge'),
      [PaymentMethod.MEMBER_NUMBER]: t('bookBulkErrorByMemberCharge'),
      [PaymentMethod.CASH]: t('bookBulkErrorByCard'),
    };
    const errorMessage =
      errorMessages[activeOption as FilteredPaymentMethod] ??
      t('bookBulkErrorByCard');

    dispatch(
      setCartId({
        brandId,
        cartId: uuidv4(),
      }),
    );
    debug('error', { createBookingError: errorMessage, formData });

    if (bookingResponse.errorType === INSUFFICIENT_RESOURCE_ERROR_TYPE) {
      setBookingError([
        {
          message: bookingResponse.error,
          code: INSUFFICIENT_RESOURCE_ERROR_TYPE,
        },
      ]);
      return;
    }

    if (cartItemsError?.length) {
      setBookingError(cartItemsError);
      return;
    }

    setBookingError(
      Array.isArray(validationMessages) && validationMessages.length
        ? validationMessages
        : [
            {
              message: errorMessage,
              code: 'booking-server-error',
            },
          ],
    );
  };

  const getLoyaltyData = async (
    paymentMethod: string,
  ): Promise<LoyaltyProgramData | undefined> => {
    if (
      (paymentMethod !== PaymentMethod.HYATT_POINTS &&
        paymentMethod !== PaymentMethod.CREDIT_CARD) ||
      !session
    ) {
      return undefined;
    }
    const hyattAuthToken = await getAccessToken();
    if (!hyattAuthToken) {
      throw new Error('Token is not available');
    }
    const memberInfo = await getMemberInfo(hyattAuthToken);
    return {
      loyaltyProgramData: {
        loyaltyProgramId: 'WOH',
        memberId: memberInfo.loyaltyId,
      },
      hyattAuthToken,
    };
  };

  const getSelectedRecommendation = (): {
    experienceId: string;
    resourceGroupCollectionId?: string;
    recommendationId: string;
  } | null => {
    try {
      const selectedListingStr = window.sessionStorage.getItem(
        'selected_recommended_listing',
      );

      if (!selectedListingStr) {
        return null;
      }

      const selectedRecommendedListing = JSON.parse(selectedListingStr);

      return {
        experienceId: selectedRecommendedListing.experienceId,
        resourceGroupCollectionId:
          selectedRecommendedListing.resourceGroupCollectionId,
        recommendationId: selectedRecommendedListing.recommendationId,
      };
    } catch (error) {
      console.error('Error getting recommendation:', error);
      return null;
    }
  };

  const doBooking = async (
    formData: ICheckoutConfig,
    integrationValidationResponse: unknown,
  ) => {
    const { activeOption } = formData;
    const { loyaltyProgramData, hyattAuthToken } =
      (await getLoyaltyData(activeOption)) || {};
    const paymentMethodConfig = paymentOptions.find((p) => {
      return p.name === activeOption;
    });
    const shouldValidateOnFullDiscount =
      !paymentMethodConfig?.shouldValidateComplimentaryBooking
        ? totalCartAmountIsFree
        : false;

    const isDetailOmit =
      shouldValidateOnFullDiscount ||
      (isIntegrationChargeOption(activeOption) &&
        (!formData.paymentName || !formData.reservationNumber));

    const isCashPayment =
      creditCodes && creditCodes.length > 0 && totalCartAmountIsFree
        ? PaymentMethod.CASH
        : activeOption;

    const bulkBookingData = {
      ...formData,
      purchaser: {
        ...formData.purchaser,
        emailAddress: formData.purchaser.emailAddress,
      },
      couponCode:
        discountCode?.discountType === DiscountType.COUPON
          ? discountCode.code
          : null,
      data: getCartItemsData({
        cartItems,
        purchaserFirstName: formData.purchaser.firstName,
        purchaserLastName: formData.purchaser.lastName,
        discountCode,
        creditCodes,
        selectedRecommendation: getSelectedRecommendation(),
      }),
      paymentMethod: isCashPayment ?? PaymentMethod.CREDIT_CARD,
      validationData: integrationValidationResponse,
      integrationType,
      paymentDetail: isDetailOmit ? null : pickPayloadData(formData),
    };
    return dispatch(
      createBulkBooking(
        brandId,
        cartId,
        bulkBookingData,
        hyattAuthToken,
        loyaltyProgramData,
        supportedLanguage,
      ),
    );
  };

  const doCheckout = async (formData: ICheckoutConfig) => {
    lockedSecretRef.current = paymentIntentSecret;
    freezePaymentIntent();

    const { activeOption, card } = formData;
    const isCreditChargeOption = isCreditCharge(activeOption);
    if (isCreditChargeOption && !card && !totalCartAmountIsFree) {
      setCreditCardError();
      unfreezePaymentIntent();
      lockedSecretRef.current = null;
      return;
    }

    let integrationValidationResponse = {};
    if (shouldValidate(formData)) {
      const { error, validationDataResponse } = await getValidationData(
        formData,
      );

      if (error) {
        debug('error', { bookingValidationError: error, formData });
        setBookingError([error] as IBookingError);
        unfreezePaymentIntent();
        lockedSecretRef.current = null;
        return;
      }

      if (validationDataResponse) {
        integrationValidationResponse = validationDataResponse;
      }
    }

    if (isCreditChargeOption) {
      if (submitCardPaymentCallback) {
        const submitResult = await submitCardPaymentCallback();
        if (!submitResult || submitResult?.error) {
          console.error(
            `submitCardPaymentCallback error: ${submitResult?.error}`,
          );
          unfreezePaymentIntent();
          lockedSecretRef.current = null;
          return;
        }
      } else {
        console.error('submitCardPaymentCallback is not defined');
      }
    }

    const bookingResponse = await doBooking(
      formData,
      integrationValidationResponse,
    );

    if (bookingResponse.type === CREATE_BULK_BOOKING_FAILURE) {
      handleBookingError(formData, bookingResponse);
      unfreezePaymentIntent();
      lockedSecretRef.current = null;
      return;
    }
    // Fire analytics event only on successful validation and submission instead of on every click
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_COMPLETE_PAYMENT_BUTTON, {
      ...(discountCode?.code && {
        discountCode: discountCode?.code,
      }),
      paymentMethod: getValues('activeOption') ?? PaymentMethod.CREDIT_CARD,
    });

    if (
      isRoomCharge(activeOption) ||
      isMemberCharge(activeOption) ||
      activeOption === PaymentMethod.HYATT_POINTS ||
      totalCartAmountIsFree
    ) {
      handlePaymentSuccess(
        bookingResponse.payload.response.subjectId,
        formData.purchaser.emailAddress,
      );
      return;
    }

    handlePaymentSubmit(
      bookingResponse.payload.response,
      formData.purchaser,
      formData,
    );
  };

  const onSubmit = (props: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(
      async (props) => {
        await doCheckout(props);
      },
      (errors) => {
        analyticsManager.sendEvent(
          ANALYTICS_EVENT.FORM_VALIDATION_ERROR,
          {
            [ANALYTICS_PROPERTY.ErrorCode]: 'checkout_form',
            [ANALYTICS_PROPERTY.ErrorMessage]: summarizeFormErrors(errors),
          },
          { providers: [IntegrationName.ADOBE_ANALYTICS] },
        );
      },
    )(props);
  };

  const isWaitingPayment = () => {
    return (
      validatingStatus === STATUS_PENDING ||
      createBulkBookingStatus === STATUS_PENDING ||
      isStripeProcessing
    );
  };

  const completeButtonLabel = useMemo(() => {
    if (isWaitingPayment()) {
      return t('waiting');
    }

    if (totalCartAmountIsFree) {
      return t('complete');
    }

    if (isCreditCharge(selectedPaymentMethod)) {
      if (
        stripeSelectedMethod === StripePaymentMethods.APPLE_PAY ||
        stripeSelectedMethod === StripePaymentMethods.GOOGLE_PAY
      ) {
        return t('payWith');
      }
      if (paymentMethodMap?.[stripeSelectedMethod]) {
        return `${t('payWith')} ${paymentMethodMap[stripeSelectedMethod]}`;
      }
      return t('payWithCreditCard');
    }
    if (isRoomCharge(selectedPaymentMethod)) {
      return t('payWithRoomCharge');
    }
    if (isMemberCharge(selectedPaymentMethod)) {
      return t('payWithMemberCharge');
    }
    if (selectedPaymentMethod === PaymentMethod.HYATT_POINTS) {
      return t('payWithHyattPoints');
    }

    return t('complete');
  }, [
    selectedPaymentMethod,
    totalCartAmountIsFree,
    stripeSelectedMethod,
    isWaitingPayment,
    t,
  ]);

  const ContinueButtonIcon = useMemo(() => {
    if (isWaitingPayment() || cartTotal === 0) {
      return null;
    }

    if (isCreditCharge(selectedPaymentMethod)) {
      if (stripeSelectedMethod === StripePaymentMethods.APPLE_PAY) {
        return ApplePayIconSvg;
      }
      if (stripeSelectedMethod === StripePaymentMethods.GOOGLE_PAY) {
        return GooglePayIconSvg;
      }
      if (stripeSelectedMethod === StripePaymentMethods.AMAZON_PAY) {
        return AmazonPayIcon;
      }
      if (stripeSelectedMethod === StripePaymentMethods.ALI_PAY) {
        return AliPayIcon;
      }
      if (stripeSelectedMethod === StripePaymentMethods.CASH_APP_PAY) {
        return CashAppIcon;
      }
      if (stripeSelectedMethod === StripePaymentMethods.LINK) {
        return LinkPayIcon;
      }
      if (stripeSelectedMethod === StripePaymentMethods.WECHAT_PAY) {
        return WeChatPayIcon;
      }
      return CardIconSvg;
    }

    return null;
  }, [
    selectedPaymentMethod,
    stripeSelectedMethod,
    cartTotal,
    isWaitingPayment,
  ]);

  const handleSignInLinkClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    const signInUrl = getHyattSignInUrl(supportedLanguage);
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.MemberLoginType]: `${document.title}:SignInButton`,
    });
    if (getCookieValue('auth-mock')) {
      setCookieValue('auth-mock', 'true');
      window.location.reload();
      return;
    }
    const { href } = new URL(window.location.href);
    localStorage.setItem(HYATT_REDIRECT_ON_LOGIN_LOCAL_KEY, href);
    window.location.href = signInUrl.toString();
  };

  const handleJoinLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    try {
      const joinUrl = getHyattJoinUrl(supportedLanguage);
      analyticsManager.setAppData({
        [ANALYTICS_PROPERTY.MemberLoginType]: `${document.title}:JoinButton`,
      });
      if (getCookieValue('auth-mock')) {
        setCookieValue('auth-mock', 'true');
        window.location.reload();
        return;
      }
      const { href } = new URL(window.location.href);
      localStorage.setItem(HYATT_REDIRECT_ON_LOGIN_LOCAL_KEY, href);
      window.location.href = joinUrl.toString();
    } catch (error) {
      console.error('Error in handleJoinLinkClick:', error);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Styled.FormSection onSubmit={onSubmit}>
          <LoadingContainer
            className={
              searchParams?.payment_intent_client_secret ? 'loading' : ''
            }
          >
            <Styled.HeadTitle
              className="page-heading"
              style={{ marginBottom: 24, fontSize: 24 }}
            >
              {t('checkout')}
            </Styled.HeadTitle>
            <PurchaserSection experiences={experienceList} />
            <PaymentContainer>
              {(lockedSecretRef.current ||
                (!isLoading && !paymentIntentError)) && (
                <PaymentSection
                  listingIds={listingIds}
                  paymentOptions={paymentOptions}
                  paymentIntentSecret={stablePaymentIntentSecret}
                  totalCartAmountIsFree={totalCartAmountIsFree}
                />
              )}
              {notEnoughPoints &&
                session &&
                selectedPaymentMethod === PaymentMethod.HYATT_POINTS && (
                  <Alert variant="error" heading={t('notEnoughPoints')}>
                    <NotEnoughPointsMessage />
                  </Alert>
                )}
              {hyattLMSEnabled && !session && areAnyEligible && (
                <Alert variant="info" heading={t('signAndJoin')}>
                  <MessageText>{t('signAndJoinDescription')}</MessageText>
                </Alert>
              )}
              {paymentIntentError?.errorType ===
                INSUFFICIENT_RESOURCE_ERROR_TYPE && (
                <PaymentValidation>
                  <ResourceSoldOutError
                    cartItems={Object.values(cartItems)}
                    message={paymentIntentError.error}
                  />
                </PaymentValidation>
              )}
              {(bookingError || bookingApprovalError) && (
                <PaymentValidation>
                  {(bookingError || bookingApprovalError)?.map(
                    (validationMessageObject) => {
                      if (
                        validationMessageObject.code ===
                        INSUFFICIENT_RESOURCE_ERROR_TYPE
                      ) {
                        return (
                          <ResourceSoldOutError
                            key={`booking-validation-message-${validationMessageObject.code}`}
                            cartItems={Object.values(cartItems)}
                            message={validationMessageObject.message}
                          />
                        );
                      }
                      return (
                        <FormError
                          key={`booking-validation-message-${validationMessageObject.code}`}
                          style={{ marginBottom: 5 }}
                        >
                          {validationMessageObject.message}
                        </FormError>
                      );
                    },
                  )}
                </PaymentValidation>
              )}
            </PaymentContainer>
            {isMobile && (
              <CartSummary
                isPayingWithPoints={
                  selectedPaymentMethod === PaymentMethod.HYATT_POINTS
                }
                paymentIntentError={paymentIntentError}
              />
            )}
            {hyattLMSEnabled && !session && areAnyEligible ? (
              <SignInButtonContainer>
                <PrimaryButton
                  big
                  onClick={handleJoinLinkClick}
                  className="lms-join-button"
                >
                  {t('joinBook')}
                </PrimaryButton>
                <SecondaryButton
                  big
                  onClick={handleSignInLinkClick}
                  className="lms-sign-in-button"
                >
                  {t('signInBook')}
                </SecondaryButton>
              </SignInButtonContainer>
            ) : (
              <PrimaryButton
                big
                className="cart__form-button-full cart__complete-checkout-button"
                type="submit"
                disabled={
                  isSubmitting ||
                  isLoading ||
                  isQuotePending ||
                  isValidating ||
                  isWaitingPayment() ||
                  bookingIsSuccessful ||
                  String(searchParams.latest).toLowerCase() === 'true' ||
                  (selectedPaymentMethod === PaymentMethod.HYATT_POINTS &&
                    !session) ||
                  (selectedPaymentMethod === PaymentMethod.HYATT_POINTS &&
                    notEnoughPoints)
                }
              >
                {completeButtonLabel}{' '}
                {ContinueButtonIcon ? (
                  <ContinueButtonIcon
                    color={theme?.colors?.buttonColorContrastShades?.[100]}
                  />
                ) : null}
              </PrimaryButton>
            )}

            {isMobile && <Sep style={{ margin: '48px 0px' }} />}
          </LoadingContainer>
        </Styled.FormSection>
      </FormProvider>
      <GuestSurveyModal
        open={surveyModalOpen}
        onClose={onCloseSurveyModal}
        cartId={cartIdForSurvey}
      />
      {!isMobile && (
        <CartSummary
          isPayingWithPoints={
            selectedPaymentMethod === PaymentMethod.HYATT_POINTS
          }
          paymentIntentError={paymentIntentError}
          findEligible={areAnyEligible}
        />
      )}
    </>
  );
};

export const PaymentContainer = styled.div``;

export const PaymentValidation = styled.div.attrs({
  role: 'alert',
  'aria-live': 'assertive',
  'aria-atomic': 'true',
})`
  margin: 15px 0;
`;

export const LoadingContainer = styled.div`
  &.loading {
    position: relative;
    min-height: 80vh;
    display: grid;
    place-items: center center;
    > * {
      opacity: 0;
      pointer-events: none;
    }
    &:before {
      margin-top: 20vh;
      content: '';
      width: 40px;
      height: 40px;
      border: 4px solid rgba(0, 0, 0, 0.2);
      border-top-color: #000;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const SignInContainer = styled.div`
  margin-bottom: 48px;
`;

export const SignInError = styled(FormError).attrs({
  role: 'alert',
  'aria-live': 'assertive',
  'aria-atomic': 'true',
})`
  margin-top: 16px;
`;

export const SignInButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

export default CheckoutPage;
